import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  reponseAPrevenirSchema,
  reponseGesteProfilSchema,
  reponseOeuvresProfilSchema,
  reponseProfilSchema,
  reponseRecalculCorrespondancesSchema,
  reponseReglagesProfilsSchema,
  reponseScoreProfilSchema,
  reponseScoresProfilsSchema,
  reponseTableauProfilsSchema,
  reponseVocabulaireProfilsSchema,
  type PasPourLuiProfil,
  type PrevenirProfil,
  type ProfilDeclare,
  type ReglagesProfils,
} from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';

const cheminGalerie = (galerie: string) => `/g/${encodeURIComponent(galerie)}`;

export const profilsApi = {
  profil: async (galerie: string, contactId: string, appel?: AppelApi) =>
    reponseProfilSchema.parse(
      await api.get(`${cheminGalerie(galerie)}/contacts/${contactId}/profil`, {
        galerie,
        ...appel,
      }),
    ),
  modifierProfil: async (
    galerie: string,
    contactId: string,
    corps: ProfilDeclare,
    appel?: AppelApi,
  ) =>
    reponseProfilSchema.parse(
      await api.put(`${cheminGalerie(galerie)}/contacts/${contactId}/profil`, corps, {
        galerie,
        ...appel,
      }),
    ),
  score: async (galerie: string, contactId: string, appel?: AppelApi) =>
    reponseScoreProfilSchema.parse(
      await api.get(`${cheminGalerie(galerie)}/contacts/${contactId}/score`, { galerie, ...appel }),
    ),
  recalculerScore: async (galerie: string, contactId: string, appel?: AppelApi) =>
    reponseScoreProfilSchema.parse(
      await api.post(
        `${cheminGalerie(galerie)}/contacts/${contactId}/score/recalcul`,
        {},
        { galerie, ...appel },
      ),
    ),
  oeuvres: async (galerie: string, contactId: string, appel?: AppelApi) =>
    reponseOeuvresProfilSchema.parse(
      await api.get(`${cheminGalerie(galerie)}/contacts/${contactId}/profil/oeuvres`, {
        galerie,
        ...appel,
      }),
    ),
  aPrevenir: async (galerie: string, oeuvreId: string, appel?: AppelApi) =>
    reponseAPrevenirSchema.parse(
      await api.get(`${cheminGalerie(galerie)}/oeuvres/${oeuvreId}/a-prevenir`, {
        galerie,
        ...appel,
      }),
    ),
  prevenir: async (
    galerie: string,
    oeuvreId: string,
    contactId: string,
    corps: PrevenirProfil,
    appel?: AppelApi,
  ) =>
    reponseGesteProfilSchema.parse(
      await api.post(
        `${cheminGalerie(galerie)}/oeuvres/${oeuvreId}/a-prevenir/${contactId}/prevenu`,
        corps,
        { galerie, ...appel },
      ),
    ),
  pasPourLui: async (
    galerie: string,
    oeuvreId: string,
    contactId: string,
    corps: PasPourLuiProfil,
    appel?: AppelApi,
  ) =>
    reponseGesteProfilSchema.parse(
      await api.post(
        `${cheminGalerie(galerie)}/oeuvres/${oeuvreId}/a-prevenir/${contactId}/pas-pour-lui`,
        corps,
        { galerie, ...appel },
      ),
    ),
  recalculerCorrespondances: async (galerie: string, oeuvreId: string, appel?: AppelApi) =>
    reponseRecalculCorrespondancesSchema.parse(
      await api.post(
        `${cheminGalerie(galerie)}/oeuvres/${oeuvreId}/a-prevenir/recalcul`,
        {},
        { galerie, ...appel },
      ),
    ),
  tableau: async (galerie: string, appel?: AppelApi) =>
    reponseTableauProfilsSchema.parse(
      await api.get(`${cheminGalerie(galerie)}/profils/a-prevenir`, { galerie, ...appel }),
    ),
  scores: async (galerie: string, ids: readonly string[], appel?: AppelApi) =>
    reponseScoresProfilsSchema.parse(
      await api.get(`${cheminGalerie(galerie)}/profils/scores?ids=${ids.join(',')}`, {
        galerie,
        ...appel,
      }),
    ),
  vocabulaire: async (galerie: string, appel?: AppelApi) =>
    reponseVocabulaireProfilsSchema.parse(
      await api.get(`${cheminGalerie(galerie)}/profils/vocabulaire`, { galerie, ...appel }),
    ),
  reglages: async (galerie: string, appel?: AppelApi) =>
    reponseReglagesProfilsSchema.parse(
      await api.get(`${cheminGalerie(galerie)}/profils/reglages`, { galerie, ...appel }),
    ),
  modifierReglages: async (galerie: string, corps: ReglagesProfils, appel?: AppelApi) =>
    reponseReglagesProfilsSchema.parse(
      await api.put(`${cheminGalerie(galerie)}/profils/reglages`, corps, { galerie, ...appel }),
    ),
};

export function useProfilContact(galerie: string, contactId: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'profils', 'contact', contactId),
    queryFn: (contexte) => profilsApi.profil(galerie, contactId, appelDeRequete(contexte)),
  });
}
export function useScoreProfil(galerie: string, contactId: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'profils', 'score', contactId),
    queryFn: (contexte) => profilsApi.score(galerie, contactId, appelDeRequete(contexte)),
  });
}
export function useOeuvresProfil(galerie: string, contactId: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'profils', 'oeuvres', contactId),
    queryFn: (contexte) => profilsApi.oeuvres(galerie, contactId, appelDeRequete(contexte)),
  });
}
export function useAPrevenir(galerie: string, oeuvreId: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'profils', 'a-prevenir', oeuvreId),
    queryFn: (contexte) => profilsApi.aPrevenir(galerie, oeuvreId, appelDeRequete(contexte)),
  });
}
export function useTableauProfils(galerie: string, enabled: boolean) {
  return useQuery({
    queryKey: cleMetier(galerie, 'profils', 'tableau'),
    queryFn: (contexte) => profilsApi.tableau(galerie, appelDeRequete(contexte)),
    enabled,
  });
}
export function useScoresProfils(galerie: string, ids: readonly string[], enabled: boolean) {
  return useQuery({
    queryKey: cleMetier(galerie, 'profils', 'scores', ids),
    queryFn: (contexte) => profilsApi.scores(galerie, ids, appelDeRequete(contexte)),
    enabled: enabled && ids.length > 0,
  });
}
export function useVocabulaireProfils(galerie: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'profils', 'vocabulaire'),
    queryFn: (contexte) => profilsApi.vocabulaire(galerie, appelDeRequete(contexte)),
  });
}
export function useReglagesProfils(galerie: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'profils', 'reglages'),
    queryFn: (contexte) => profilsApi.reglages(galerie, appelDeRequete(contexte)),
  });
}

// Chaque geste rafraîchit aussi les surfaces alimentées par les correspondances.
export function useGesteProfil<T>(
  galerie: string,
  executer: (corps: T, appel: AppelApi) => Promise<unknown>,
) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: executer,
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'profils') });
        void client.invalidateQueries({ queryKey: cleMetier(galerie, 'contacts', 'liste') });
      },
    }),
  );
}
